/**
 * Geometry of a tilted ("3D") donut: the ring is drawn as an ellipse seen from above at an angle, with an outer
 * wall below its front half and the inner wall of the hole visible behind it. Slice angles are exactly
 * proportional to the values; only the projection is tilted. Angles grow clockwise on screen, the first slice
 * starts at 12 o'clock.
 */

export interface DonutGeometry {
  cx: number;
  cy: number;
  /** Horizontal radius of the outer ellipse. */
  rx: number;
  /** Vertical radius (rx × tilt). */
  ry: number;
  /** Inner radius as a share of the outer one (0 = pie). */
  inner: number;
  /** Height of the walls in px. */
  depth: number;
}

export interface SliceAngles {
  start: number;
  end: number;
}

const START = -Math.PI / 2;
const TAU = Math.PI * 2;

/** Cumulative angles of the slices; zero values give empty slices. Throws on negative values. */
export function sliceAngles(values: number[]): SliceAngles[] {
  if (values.some((v) => v < 0 || !Number.isFinite(v))) throw new Error('slice values must be finite and >= 0');
  const total = values.reduce((s, v) => s + v, 0);
  let angle = START;
  return values.map((v) => {
    const span = total > 0 ? (v / total) * TAU : 0;
    const slice = { start: angle, end: angle + span };
    angle += span;
    return slice;
  });
}

/** Intersections of [start, end] with the given intervals (angles in the range START … START + 2π). */
function clip(start: number, end: number, intervals: [number, number][]): [number, number][] {
  return intervals
    .map(([a, b]) => [Math.max(start, a), Math.min(end, b)] as [number, number])
    .filter(([a, b]) => b - a > 1e-9);
}

/** Where the outer wall faces the viewer: the front (lower) half of the ellipse, sin θ > 0. */
const FRONT: [number, number][] = [[0, Math.PI]];
/** Where the inner wall of the hole is visible: its back (upper) half, sin θ < 0. */
const BACK: [number, number][] = [
  [-Math.PI, 0],
  [Math.PI, TAU],
];

const f = (n: number) => Math.round(n * 100) / 100;

function point(g: DonutGeometry, angle: number, scale: number, dy = 0): string {
  return `${f(g.cx + scale * g.rx * Math.cos(angle))} ${f(g.cy + scale * g.ry * Math.sin(angle) + dy)}`;
}

function arc(g: DonutGeometry, scale: number, from: number, to: number, sweep: 0 | 1, dy = 0): string {
  const large = Math.abs(to - from) > Math.PI ? 1 : 0;
  return `A ${f(scale * g.rx)} ${f(scale * g.ry)} 0 ${large} ${sweep} ${point(g, to, scale, dy)}`;
}

/** The top face of a slice (a ring segment, or a pie wedge when inner = 0). */
export function topPath(g: DonutGeometry, s: SliceAngles): string {
  if (s.end - s.start <= 1e-9) return '';
  // a full ring cannot be one SVG arc: split it in two halves
  if (s.end - s.start >= TAU - 1e-9) {
    const mid = s.start + Math.PI;
    return `${topPath(g, { start: s.start, end: mid })} ${topPath(g, { start: mid, end: s.end })}`;
  }
  const outer = `M ${point(g, s.start, 1)} ${arc(g, 1, s.start, s.end, 1)}`;
  if (g.inner <= 0) return `${outer} L ${g.cx} ${g.cy} Z`;
  return `${outer} L ${point(g, s.end, g.inner)} ${arc(g, g.inner, s.end, s.start, 0)} Z`;
}

function wall(g: DonutGeometry, scale: number, a: number, b: number): string {
  return `M ${point(g, a, scale)} ${arc(g, scale, a, b, 1)} L ${point(g, b, scale, g.depth)} ${arc(g, scale, b, a, 0, g.depth)} Z`;
}

/** The visible part of the slice's outer wall (front half only); empty when the slice lies at the back. */
export function outerWallPath(g: DonutGeometry, s: SliceAngles): string {
  return clip(s.start, s.end, FRONT)
    .map(([a, b]) => wall(g, 1, a, b))
    .join(' ');
}

/** The visible part of the inner wall of the hole (back half only); empty for a pie. */
export function innerWallPath(g: DonutGeometry, s: SliceAngles): string {
  if (g.inner <= 0) return '';
  return clip(s.start, s.end, BACK)
    .map(([a, b]) => wall(g, g.inner, a, b))
    .join(' ');
}

/** Where to put the label of a slice: the middle of the ring at the slice's middle angle. */
export function labelPoint(g: DonutGeometry, s: SliceAngles): { x: number; y: number } {
  const mid = (s.start + s.end) / 2;
  const scale = (1 + g.inner) / 2;
  return { x: f(g.cx + scale * g.rx * Math.cos(mid)), y: f(g.cy + scale * g.ry * Math.sin(mid)) };
}

/** A darker shade of a #rrggbb colour (the walls), mixing with black. */
export function shade(hex: string, amount: number): string {
  const n = parseInt(hex.slice(1), 16);
  const ch = (shift: number) => Math.round(((n >> shift) & 255) * (1 - amount));
  return `#${[16, 8, 0].map((s) => ch(s).toString(16).padStart(2, '0')).join('')}`;
}

/**
 * Categorical slice colours in fixed order (validated: every adjacent pair incl. last ↔ first clears the CVD and
 * normal-vision floors on the white ID-SK surface; aqua, yellow and magenta are below 3:1, so every chart has a
 * data table). The neutral grey is reserved for the "Ostatné" (other) slice.
 */
export const SLICE_COLOURS = ['#2a78d6', '#eb6834', '#1baf7a', '#eda100', '#e87ba4', '#008300'] as const;
export const OTHER_COLOUR = '#7d7b77';
